import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Buildings, ClockClockwise, Download } from "@phosphor-icons/react";
import { useSelection } from "@/hooks/useSelection";
import { useConfirm } from "@/hooks/useConfirm";
import BulkActionBar, { SelectAllCheckbox, RowCheckbox, runBulkDelete, BulkBarButton } from "@/components/BulkActionBar";
import { exportRowsToCsv, todayStamp } from "@/lib/csvExport";

const empty = { nombre: "", direccion: "", telefono: "", encargado: "" };

export default function Sucursales({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const navigate = useNavigate();
  const selection = useSelection(items);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const onBulkDelete = async () => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    const ok = await confirm({
      title: `Eliminar ${ids.length} sucursal${ids.length === 1 ? "" : "es"}`,
      description: "Los productos, ventas y usuarios vinculados a estas sucursales quedarán sin sucursal asignada (no se borran). Verifica antes de confirmar.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkDelete(ids, (id) => api.delete(`/sucursales/${id}`));
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} sucursal(es) eliminada(s)`);
    if (fail) toast.error(`${fail} no se pudieron eliminar`);
    load();
  };

  const onBulkExport = () => {
    const ids = new Set(selection.selectedIds);
    const rows = items.filter((s) => ids.has(s.id));
    if (!rows.length) return;
    exportRowsToCsv({
      filename: `sucursales-${todayStamp()}.csv`,
      headers: [
        { key: "nombre", label: "Nombre" },
        { key: "encargado", label: "Encargado" },
        { key: "direccion", label: "Dirección" },
        { key: "telefono", label: "Teléfono" },
      ],
      rows,
    });
    toast.success(`${rows.length} sucursal(es) exportada(s) a CSV`);
  };

  const viewHistory = (s) => {
    const params = new URLSearchParams({
      entity: "sucursales",
      entity_id: s.id,
      label: s.nombre || "",
    });
    navigate(`/auditoria?${params.toString()}`);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sucursales");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return toast.error("Nombre requerido");
    setLoading(true);
    try {
      if (editingId) {
        await api.put(`/sucursales/${editingId}`, form);
        toast.success("Sucursal actualizada");
      } else {
        await api.post("/sucursales", form);
        toast.success("Sucursal creada");
      }
      setForm(empty);
      setEditingId(null);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      nombre: s.nombre || "",
      direccion: s.direccion || "",
      telefono: s.telefono || "",
      encargado: s.encargado || "",
    });
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar sucursal?")) return;
    try {
      await api.delete(`/sucursales/${id}`);
      toast.success("Eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className={embedded ? "" : "px-4 sm:px-8 lg:px-16 py-8 max-w-[1100px] mx-auto"}>
      {!embedded && (
        <header className="mb-8">
          <div className="overline text-klein">Configuración</div>
          <h1 className="h-display text-4xl font-black tracking-tighter">Sucursales</h1>
          <p className="text-sm text-muted2 mt-2 max-w-xl">
            Registra cada punto físico o lógico para asignar ventas, usuarios y reportes por sucursal.
          </p>
        </header>
      )}

      <section className="border border-zinc-200 p-6 mb-8" data-testid="sucursal-form-section">
        <div className="flex items-center gap-2 mb-4">
          <Buildings size={18} className="text-klein" />
          <div className="text-sm font-medium">
            {editingId ? "Editar sucursal" : "Nueva sucursal"}
          </div>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Nombre" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} testid="sucursal-nombre" required help="Nombre comercial o identificador interno (ej: 'Centro CDMX', 'Bodega Norte'). Aparecerá en reportes y selectores." />
          <Input label="Encargado" value={form.encargado} onChange={(v) => setForm({ ...form, encargado: v })} testid="sucursal-encargado" help="Persona responsable del lugar. Útil para enviarle reportes o contactarla por WhatsApp." />
          <Input label="Dirección" value={form.direccion} onChange={(v) => setForm({ ...form, direccion: v })} testid="sucursal-direccion" help="Dirección física. Se incluye en facturas/recibos y para coordinación de envíos." />
          <Input label="Teléfono" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} testid="sucursal-telefono" help="Número directo de la sucursal (con lada). Se mostrará a los compradores cuando sea relevante." />
          <div className="sm:col-span-2 flex gap-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="sucursal-submit-btn"
              data-help={editingId ? "Guarda los cambios de esta sucursal." : "Registra una nueva sucursal y la deja disponible en los filtros y al asignar usuarios."}
              className="bg-klein text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-klein/90 disabled:opacity-60"
            >
              <Plus size={16} /> {editingId ? "Guardar cambios" : "Agregar sucursal"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(empty);
                }}
                data-help="Descarta los cambios y vuelve al modo 'crear nueva sucursal'."
                className="border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </section>

      <BulkActionBar
        count={selection.count}
        noun="sucursal"
        pluralNoun="sucursales"
        onDelete={onBulkDelete}
        onClear={selection.clear}
        busy={bulkBusy}
        testid="sucursales-bulk-bar"
        extraActions={
          <BulkBarButton
            onClick={onBulkExport}
            icon={Download}
            label="Exportar CSV"
            testid="sucursales-bulk-bar-export"
            disabled={bulkBusy}
          />
        }
      />
      {confirmDialog}

      <section className="border border-zinc-200">
        <div className="px-4 py-3 border-b border-zinc-200 text-sm font-medium flex items-center gap-3">
          {items.length > 0 && (
            <SelectAllCheckbox
              allSelected={selection.allSelected}
              someSelected={selection.someSelected}
              onChange={selection.toggleAll}
              testid="sucursales-select-all"
            />
          )}
          <span>{items.length} sucursales</span>
        </div>
        <div className="divide-y divide-zinc-100">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-muted2 text-sm">
              Aún no hay sucursales registradas.
            </div>
          )}
          {items.map((s) => (
            <div
              key={s.id}
              className={`px-4 py-3 flex items-center justify-between gap-3 ${selection.isSelected(s.id) ? "bg-[#EEF2FF]" : ""}`}
              data-testid={`sucursal-row-${s.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <RowCheckbox
                  checked={selection.isSelected(s.id)}
                  onChange={() => selection.toggle(s.id)}
                  testid={`select-sucursal-${s.id}`}
                />
                <div className="min-w-0">
                  <div className="font-medium">{s.nombre}</div>
                  <div className="text-xs text-muted2">
                    {s.encargado ? `Encargado: ${s.encargado}` : ""}
                    {s.direccion ? ` · ${s.direccion}` : ""}
                    {s.telefono ? ` · ${s.telefono}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => viewHistory(s)}
                  className="border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50 flex items-center gap-1"
                  data-testid={`sucursal-history-${s.id}`}
                  data-help="Ver el historial de cambios de esta sucursal en la auditoría."
                >
                  <ClockClockwise size={12} /> Historial
                </button>
                <button
                  onClick={() => startEdit(s)}
                  className="border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50 flex items-center gap-1"
                  data-testid={`sucursal-edit-${s.id}`}
                  data-help="Carga los datos de esta sucursal en el formulario de arriba para modificarlos."
                >
                  <PencilSimple size={12} /> Editar
                </button>
                <button
                  onClick={() => remove(s.id)}
                  className="border border-red-200 text-red-600 px-3 py-1 text-xs hover:bg-red-50 flex items-center gap-1"
                  data-testid={`sucursal-delete-${s.id}`}
                  data-help="Elimina la sucursal. Sus ventas y productos quedarán sin sucursal asignada (pide confirmación)."
                >
                  <Trash size={12} /> Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Input({ label, value, onChange, testid, required, help }) {
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      <input
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        data-help={help}
        className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-klein outline-none"
      />
    </div>
  );
}
